import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { formatDateToYYYYMMDD } from '../../utils/date'
import {
	CONTRACT_TYPE_LABEL,
	REGION_LABEL,
	compensationBlocks,
	contractPeriodLine,
	formatRelativeTimeKo,
	stripHtml,
} from '../../utils/jobPostCardDisplay'
import styles from './JobPostPublicListView.module.scss'

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

const JobPostPublicListView = () => {
	const [list, setList] = useState([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		let cancelled = false
		;(async () => {
			try {
				const res = await axios.get(`${API_ENDPOINT}/job-post`)
				if (!cancelled) setList(Array.isArray(res.data) ? res.data : [])
			} catch {
				if (!cancelled) setList([])
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [])

	const sorted = useMemo(() => {
		return [...list].sort((a, b) => {
			const ta = new Date(a.created_at ?? a.createdAt ?? 0).getTime()
			const tb = new Date(b.created_at ?? b.createdAt ?? 0).getTime()
			return tb - ta
		})
	}, [list])

	if (loading) {
		return (
			<div className={styles.page}>
				<div className={styles.hero}>
					<span className={styles.heroEyebrow}>Recruitment</span>
					<h1 className={styles.heroTitle}>채용 공고</h1>
					<p className={styles.heroDesc}>공고 목록을 불러오는 중입니다.</p>
				</div>
				<div className={styles.loading} aria-hidden>
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
				</div>
			</div>
		)
	}

	return (
		<div className={styles.page}>
			<header className={styles.hero}>
				<span className={styles.heroEyebrow}>Recruitment</span>
				<h1 className={styles.heroTitle}>채용 공고</h1>
				<p className={styles.heroDesc}>
					투어 프로가 등록한 캐디 모집 공고입니다. 카드를 눌러 상세 내용을 확인할 수
					있습니다.
				</p>
			</header>

			{sorted.length === 0 ? (
				<div className={styles.empty}>
					<p className={styles.emptyTitle}>등록된 공고가 없습니다</p>
					<p>새 공고가 올라오면 이곳에 표시됩니다.</p>
				</div>
			) : (
				<ul className={styles.list}>
					{sorted.map((row) => {
						const id = row.id
						const subject = row.postSubject ?? row.post_subject ?? '(제목 없음)'
						const brief = stripHtml(row.postContent ?? row.post_content)
						const contractType = row.contract_type ?? row.contractType
						const region = row.region
						const status = row.status
						const isClosed = status === 'closed'
						const userName = row.user?.name ?? row.writerName
						const createdRaw = row.createdAt ?? row.created_at
						const createdLabel = createdRaw
							? formatDateToYYYYMMDD(new Date(createdRaw))
							: ''
						const relativePosted = formatRelativeTimeKo(createdRaw)
						const typeLabel =
							CONTRACT_TYPE_LABEL[contractType] ?? contractType ?? '계약'
						const regionLabel = region ? (REGION_LABEL[region] ?? region) : null
						const payBlocks = compensationBlocks(row)
						const periodLine = contractPeriodLine(row)

						return (
							<li key={id}>
								<Link
									to={`/posts/${id}`}
									className={`${styles.card} ${isClosed ? styles.cardClosed : ''}`}>
									{createdLabel || relativePosted ? (
										<div className={styles.cardTopMeta}>
											{createdLabel ? (
												<span className={styles.date}>
													등록 {createdLabel}
												</span>
											) : null}
											{createdLabel && relativePosted ? (
												<span className={styles.topMetaSep} aria-hidden>
													·
												</span>
											) : null}
											{relativePosted ? (
												<span className={styles.relativeTime}>
													{relativePosted}
												</span>
											) : null}
										</div>
									) : null}
									<div className={styles.cardHeader}>
										<h2 className={styles.cardTitle}>{subject}</h2>
										<ChevronRightIcon
											className={styles.cardArrow}
											aria-hidden
										/>
									</div>
									<div className={styles.metaRow}>
										<span className={`${styles.badge} ${styles.badgeType}`}>
											{typeLabel}
										</span>
										{regionLabel ? (
											<span
												className={`${styles.badge} ${
													region === 'INTERNATIONAL'
														? styles.badgeRegionOverseas
														: styles.badgeRegionDomestic
												}`}>
												{regionLabel}
											</span>
										) : null}
										{isClosed ? (
											<span
												className={`${styles.badge} ${styles.badgeClosed}`}>
												마감
											</span>
										) : null}
										{userName ? (
											<span className={styles.writer}>{userName} 프로</span>
										) : null}
									</div>
									{payBlocks.length > 0 ? (
										<div className={styles.compensation}>
											{payBlocks.map((b) => (
												<div key={b.key} className={styles.compItem}>
													<span className={styles.compLabel}>
														{b.label}
													</span>
													<span className={styles.compValue}>
														{b.value}
														<span className={styles.compUnit}>
															{b.unit}
														</span>
													</span>
												</div>
											))}
										</div>
									) : null}
									{brief ? <p className={styles.brief}>{brief}</p> : null}
									{periodLine ? (
										<p className={styles.periodHint}>{periodLine}</p>
									) : null}
								</Link>
							</li>
						)
					})}
				</ul>
			)}
		</div>
	)
}

export default JobPostPublicListView
